/**
 * Impersonation Store
 * Manages admin "View As" functionality to view the platform as another user
 *
 * Security Features:
 * - Stored in sessionStorage (cleared on tab close)
 * - Only admins can impersonate (enforced server-side)
 * - Cannot impersonate other admins (enforced server-side)
 * - All actions are audit logged server-side
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

interface ImpersonatedUser {
  id: string;
  email: string;
  displayName: string | null;
}

interface AdminUser {
  id: string;
  email: string;
  name: string;
}

interface ImpersonationStore {
  // State
  isImpersonating: boolean;
  impersonatedUser: ImpersonatedUser | null;
  adminUser: AdminUser | null;

  // Actions
  startImpersonation: (admin: AdminUser, targetUser: ImpersonatedUser) => void;
  stopImpersonation: () => void;
  clearImpersonation: () => void;
}

export const useImpersonationStore = create<ImpersonationStore>()(
  persist(
    (set) => ({
      // Initial state - not impersonating
      isImpersonating: false,
      impersonatedUser: null,
      adminUser: null,

      // Start impersonating a user
      startImpersonation: (admin, targetUser) => {
        console.log('[Impersonation] Starting impersonation', {
          admin: admin.email,
          target: targetUser.email
        });
        set({
          isImpersonating: true,
          impersonatedUser: targetUser,
          adminUser: admin
        });
      },

      // Stop impersonation and return to admin view
      stopImpersonation: () => {
        console.log('[Impersonation] Stopping impersonation');
        set({
          isImpersonating: false,
          impersonatedUser: null,
          adminUser: null
        });
      },

      // Clear all impersonation state (for logout)
      clearImpersonation: () => {
        set({
          isImpersonating: false,
          impersonatedUser: null,
          adminUser: null
        });
      }
    }),
    {
      name: 'impersonation-store',
      // Use sessionStorage so impersonation is cleared when tab closes
      storage: createJSONStorage(() => sessionStorage)
    }
  )
);

/**
 * Hook to get the impersonation header for API requests
 * Returns the X-Impersonate-User header value if impersonating, null otherwise
 */
export const useImpersonationHeader = (): string | null => {
  const { isImpersonating, impersonatedUser } = useImpersonationStore();

  if (isImpersonating && impersonatedUser) {
    return impersonatedUser.id;
  }

  return null;
};

/**
 * Get impersonation user ID directly (for use in API interceptor)
 * This is a non-reactive getter for use in axios interceptors
 */
export const getImpersonationUserId = (): string | null => {
  const state = useImpersonationStore.getState();

  if (state.isImpersonating && state.impersonatedUser) {
    return state.impersonatedUser.id;
  }

  return null;
};
